/* Ostrich Designs — theme.js
   Vanilla JS, progressive enhancement only. No external libraries.
   Facets, cart and the clean pill all work with JS disabled; this
   layer removes full page reloads where it can. */

(function () {
  "use strict";

  var TIER_COOKIE = "ostrich_tier";

  /* Conversational AI search worker (ostrich-search/). POST { query, tier },
     see README there for the full response contract. Fixed infra, not a
     theme setting - CORS on the worker side is locked to the storefront's
     own domains regardless. */
  var AI_SEARCH_URL = "https://ostrich-search.alan-0a3.workers.dev/search";
  var AI_SEARCH_TIMEOUT_MS = 4000;
  var AI_SEARCH_MAX_QUERY_LENGTH = 200;

  /* Set by initFacets() once it has found a [data-facets-results]
     container on this page, so initDial() can reuse the exact same
     fetch-and-swap path for the Dial's own facet navigation instead of a
     full page reload - see the Dial section below for why. */
  var swapFacetResults = null;

  /* ---------- Small number "tick" helper ----------
     Used only where a visible count changes without a full page
     navigation (currently: the Dial changing the tier filter on a
     collection/search page). Finds the first run of digits in the old
     and new text and animates between them over `duration`ms, leaving
     the rest of the string (locale copy, pluralisation) untouched.
     Falls back to a hard swap when there is nothing sensible to animate
     or the visitor asked for reduced motion. */
  function tickNumberText(el, newText, duration) {
    if (!el) return;
    var oldText = el.textContent || "";
    var oldMatch = oldText.match(/\d+/);
    var newMatch = newText.match(/\d+/);
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!oldMatch || !newMatch || reduced) {
      el.textContent = newText;
      return;
    }
    var from = parseInt(oldMatch[0], 10);
    var to = parseInt(newMatch[0], 10);
    if (from === to) {
      el.textContent = newText;
      return;
    }
    var settled = false;
    function finish() {
      if (settled) return;
      settled = true;
      el.textContent = newText;
    }
    var start = null;
    function step(ts) {
      if (settled) return;
      if (start === null) start = ts;
      var progress = Math.min((ts - start) / duration, 1);
      var current = Math.round(from + (to - from) * progress);
      el.textContent = newText.replace(/\d+/, String(current));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        finish();
      }
    }
    requestAnimationFrame(step);
    /* requestAnimationFrame can stall (backgrounded tab, throttled timers)
       and leave the count stuck mid-animation with no way back to the
       correct value. This guarantees it lands on newText regardless. */
    setTimeout(finish, duration + 100);
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
  }

  function setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + d.toUTCString();
    }
    document.cookie = name + "=" + encodeURIComponent(value) + expires + "; path=/; samesite=Lax";
  }

  /* ---------- Shared: locale strings + product-card rendering ----------
     Both the AI search results panel and the taxonomy-driven
     recommendations/cross-lane fetch (below) render product cards
     client-side by cloning [data-product-card-template] (see
     sections/header.liquid) and filling named [data-field] slots via
     textContent/attribute assignment only - never innerHTML - so nothing
     from a Worker or Storefront API response is ever parsed as HTML.
     This is the one implementation both features share, so a client-
     rendered card always matches snippets/product-card.liquid's markup. */

  var ostrichStrings = null;
  function getOstrichStrings() {
    if (ostrichStrings) return ostrichStrings;
    ostrichStrings = {};
    var el = document.querySelector("[data-ostrich-strings]");
    if (el) {
      try {
        ostrichStrings = JSON.parse(el.textContent) || {};
      } catch (e) {
        ostrichStrings = {};
      }
    }
    return ostrichStrings;
  }

  function tierLabelFor(tier) {
    var strings = getOstrichStrings();
    if (tier === "banter") return strings.tierBanter || "Banter";
    if (tier === "explicit") return strings.tierExplicit || "Explicit";
    return strings.tierClean || "Clean";
  }

  function safeJsonParse(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* Deterministic 1-6 card colour-block index from a product handle, for
     visual parity with the grid's own assignment (snippets/card-color
     -index.liquid, an md5-of-handle hash). This is a plain character-sum
     hash, not MD5 - close enough for a decorative background block picked
     from a handful of near-identical low-saturation tones, and avoids
     shipping an MD5 implementation into theme.js for that alone. */
  function hashHandle(handle) {
    var str = String(handle || "");
    var sum = 0;
    for (var i = 0; i < str.length; i++) sum += str.charCodeAt(i);
    return (sum % 6) + 1;
  }

  function formatMoney(amount, currencyCode) {
    var num = parseFloat(amount);
    if (isNaN(num) || !currencyCode) return "";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
        style: "currency",
        currency: currencyCode,
      }).format(num);
    } catch (e) {
      return currencyCode + " " + num.toFixed(2);
    }
  }

  /* fields: { handle, url, tier, imageUrl, imageUrl2, facetLabel, title, price, currencyCode }
     opts.deferImage: withholds the main image src (sets data-src instead,
     and drops the hover-swap image entirely) - mirrors snippets/product
     -card.liquid's defer_image param, used by the cross-lane panel so no
     thumbnail is requested before the visitor opens it (theme.js
     initCrossLane reveals data-src images on open, unchanged). */
  function buildProductCard(fields, opts) {
    opts = opts || {};
    var template = document.querySelector("[data-product-card-template]");
    if (!template || !template.content) return document.createDocumentFragment();
    var frag = template.content.cloneNode(true);
    var a = frag.querySelector("a.product-card");
    var tier = fields.tier === "banter" || fields.tier === "explicit" ? fields.tier : "clean";
    var title = fields.title || "";

    a.href = fields.url || "#";
    a.setAttribute("data-tier", tier);
    a.setAttribute("data-card", String(hashHandle(fields.handle)));
    a.setAttribute("aria-label", title ? "View " + title : "View product");

    var img = frag.querySelector('[data-field="image"]');
    var imgAlt = frag.querySelector('[data-field="image-alt"]');
    if (opts.deferImage) {
      if (imgAlt) imgAlt.remove();
      if (img) {
        if (fields.imageUrl) {
          img.setAttribute("data-src", fields.imageUrl);
          img.alt = title;
        } else {
          img.remove();
        }
      }
    } else {
      if (img) {
        if (fields.imageUrl) {
          img.src = fields.imageUrl;
          img.alt = title;
        } else {
          img.remove();
        }
      }
      if (imgAlt) {
        if (fields.imageUrl2) {
          imgAlt.src = fields.imageUrl2;
        } else {
          imgAlt.remove();
        }
      }
    }

    var tierChip = frag.querySelector('[data-field="tier-chip"]');
    if (tierChip) {
      tierChip.textContent = tierLabelFor(tier);
      tierChip.classList.add("tier-chip--" + tier);
    }

    var facetEl = frag.querySelector('[data-field="facet"]');
    if (facetEl) {
      if (fields.facetLabel) {
        facetEl.textContent = fields.facetLabel;
      } else {
        facetEl.remove();
      }
    }

    var captionEl = frag.querySelector('[data-field="caption"]');
    if (captionEl) captionEl.textContent = title;

    var priceEl = frag.querySelector('[data-field="price"]');
    if (priceEl) {
      var money = fields.price != null ? formatMoney(fields.price, fields.currencyCode) : "";
      if (money) {
        priceEl.textContent = money;
      } else {
        priceEl.remove();
      }
    }

    return frag;
  }

  /* ---------- Mobile nav ---------- */

  function initMobileNav() {
    var toggle = document.querySelector("[data-mobile-nav-toggle]");
    var nav = document.querySelector("[data-mobile-nav]");
    if (!toggle || !nav) return;
    toggle.addEventListener("click", function () {
      var open = nav.getAttribute("data-open") === "true";
      nav.setAttribute("data-open", open ? "false" : "true");
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
    });
  }

  /* ---------- The Dial ----------
     Three stops, real radios (snippets/dial.liquid), one native radio
     group per Dial instance with three distinct values (clean / banter /
     explicit) - no shared name/value tricks, so keyboard grouping and
     screen readers see exactly what is there. No cookie means no stop is
     JS-checked, so the static "Not safe" default (set in the HTML) is
     what a first-time visitor sees. With JS, a change:
       1. sets the ostrich_tier cookie so the choice persists site-wide,
       2. flags <html> so the CSS tier guard (theme.css) applies
          instantly, everywhere else on the page, with no reload,
       3. crossfades the mascot to match,
       4. on a collection/search page, navigates to data-dial-url, the
          exact same fully-correct URL (built once, server-side, in
          dial.liquid) that the <noscript> fallback links use - this is
          the one case a full page load is unavoidable, because the
          result set genuinely changes server-side.
     Multiple Dials can exist on one page (header + homepage hero); a
     change in either one syncs the other so they never disagree. */

  function applyTierClass(stop) {
    var html = document.documentElement;
    html.classList.remove("tier-clean", "tier-pub");
    if (stop === "clean") html.classList.add("tier-clean");
    else if (stop === "banter") html.classList.add("tier-pub");
  }

  function syncDials(stop) {
    document.querySelectorAll("[data-dial]").forEach(function (dial) {
      dial.querySelectorAll("[data-dial-input]").forEach(function (input) {
        input.checked = input.value === stop;
      });
    });
  }

  function initDial() {
    var dials = document.querySelectorAll("[data-dial]");
    if (!dials.length) return;
    var current = getCookie(TIER_COOKIE);

    if (current) {
      syncDials(current);
      applyTierClass(current);
    }

    dials.forEach(function (dial) {
      var isFacet = dial.getAttribute("data-dial-facet") === "true";
      var inputs = dial.querySelectorAll("[data-dial-input]");
      inputs.forEach(function (input) {
        input.addEventListener("change", function () {
          var stop = input.value;
          setCookie(TIER_COOKIE, stop, 365);
          applyTierClass(stop);
          syncDials(stop);
          if (isFacet) {
            var url = input.getAttribute("data-dial-url");
            /* Prefer the same in-place fetch-and-swap the other facets
               use (initFacets, below) over a full reload, so the results
               count can tick from old to new (DESIGN-DIRECTION-V2.md:
               "The count changing is the feedback") instead of jumping
               via a page load. Falls back to the plain navigation - the
               fully correct URL either way, built once in dial.liquid -
               if that path isn't available for any reason. */
            var resultsEl = document.querySelector("[data-facets-results]");
            if (typeof swapFacetResults === "function" && resultsEl) {
              /* The visible result count (search-results__toolbar's
                 .results-count, or collection-grid's .toolbar count) is
                 deliberately NOT inside [data-facets-results] - it sits
                 next to the search field/sort control, on its own row,
                 per the search-results heading fix. So it never gets
                 touched by the innerHTML swap below; it has to be found
                 and updated separately, from the freshly fetched
                 document fetchAndSwap hands back. */
              var countEl = document.querySelector(".results-count, .toolbar .mono.small");
              var oldCountText = countEl ? countEl.textContent : null;
              swapFacetResults(url, true, function (doc) {
                if (!oldCountText || !doc) return;
                var newCountSource = doc.querySelector(".results-count, .toolbar .mono.small");
                var liveCountEl = document.querySelector(".results-count, .toolbar .mono.small");
                if (!newCountSource || !liveCountEl) return;
                var newText = newCountSource.textContent;
                liveCountEl.textContent = oldCountText;
                tickNumberText(liveCountEl, newText, 300);
              });
            } else {
              window.location.href = url;
            }
          }
        });
      });
    });
  }

  /* ---------- Homepage: header Dial hidden behind the hero Dial ----------
     sections/header.liquid adds .site-header--dial-hidden server-side on
     template.name == 'index' only, so the header Dial starts (and, with
     JS off, stays) collapsed - the hero's own size:"large" Dial is always
     present there, so nothing is lost. This just takes the class live:
     hide the header Dial while the hero is in the viewport, reveal it
     once the hero has scrolled past (and re-hide on scrolling back up).
     Both Dials already share the ostrich_tier cookie and syncDials(), so
     whichever one is visible always reflects the same selected stop. */

  function initHeaderDialSync() {
    var header = document.querySelector(".site-header");
    var hero = document.querySelector("[data-hero]");
    if (!header || !hero || !header.classList.contains("site-header--dial-hidden")) return;

    if (!("IntersectionObserver" in window)) {
      /* No IO support: never leave the header Dial permanently hidden
         with no way for it to come back. */
      header.classList.remove("site-header--dial-hidden");
      return;
    }

    /* The sticky header still covers the top of the viewport while
       scrolling, so "the hero has scrolled past" means its bottom edge
       has cleared the bottom of the (currently nav-row-only, since the
       Dial bar is collapsed) sticky header - not the raw viewport top. */
    var headerHeight = header.getBoundingClientRect().height;
    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        header.classList.toggle("site-header--dial-hidden", entry.isIntersecting);
      },
      { rootMargin: "-" + headerHeight + "px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(hero);
  }

  /* ---------- Facets: enhance to avoid full reloads ---------- */

  function initFacets() {
    var form = document.querySelector("[data-facets-form]");
    var results = document.querySelector("[data-facets-results]");
    if (!form || !results) return;

    var facetsToggle = document.querySelector("[data-facets-toggle]");
    var facetsPanel = document.querySelector("[data-facets-panel]");
    if (facetsToggle && facetsPanel) {
      facetsToggle.addEventListener("click", function () {
        var open = facetsPanel.getAttribute("data-open") === "true";
        facetsPanel.setAttribute("data-open", open ? "false" : "true");
        facetsToggle.setAttribute("aria-expanded", open ? "false" : "true");
      });
    }

    function showSkeleton() {
      results.setAttribute("aria-busy", "true");
    }

    function fetchAndSwap(url, pushState, onSwapped) {
      showSkeleton();
      fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var next = doc.querySelector("[data-facets-results]");
          var nextFacets = doc.querySelector("[data-facets-panel]");
          var nextActive = doc.querySelector("[data-active-filters]");
          if (next) {
            results.innerHTML = next.innerHTML;
            results.removeAttribute("aria-busy");
          }
          if (nextFacets && facetsPanel) {
            facetsPanel.innerHTML = nextFacets.innerHTML;
          }
          var active = document.querySelector("[data-active-filters]");
          if (nextActive && active) {
            active.innerHTML = nextActive.innerHTML;
          }
          if (pushState) window.history.pushState({}, "", url);
          bindResultLinks();
          if (typeof onSwapped === "function") onSwapped(doc);
        })
        .catch(function () {
          window.location.href = url;
        });
    }

    /* Exposed so initDial() can drive the same in-place swap for the
       Dial's own tier-filter navigation (see initDial). */
    swapFacetResults = fetchAndSwap;

    function bindResultLinks() {
      document.querySelectorAll("[data-facet-link]").forEach(function (link) {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          fetchAndSwap(link.href, true);
        });
      });
      var sortField = document.querySelector("[data-sort-select]");
      if (sortField && sortField.form) {
        sortField.addEventListener("change", function () {
          var formData = new URLSearchParams(new FormData(sortField.form));
          var url = sortField.form.getAttribute("action") + "?" + formData.toString();
          fetchAndSwap(url, true);
        });
      }
      var pageLinks = document.querySelectorAll("[data-page-link]");
      pageLinks.forEach(function (link) {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          fetchAndSwap(link.href, true);
          window.scrollTo({ top: results.offsetTop - 96, behavior: "auto" });
        });
      });
    }

    bindResultLinks();

    window.addEventListener("popstate", function () {
      fetchAndSwap(window.location.href, false);
    });
  }

  /* ---------- Product card photo swap on keyboard focus (mirrors CSS hover) ---------- */

  function initCardMedia() {
    document.querySelectorAll(".product-card__media").forEach(function (media) {
      var altImg = media.querySelector("img.is-alt");
      if (!altImg) return;
      media.closest("a").addEventListener("focus", function () {
        altImg.style.opacity = "1";
      });
      media.closest("a").addEventListener("blur", function () {
        altImg.style.opacity = "0";
      });
    });
  }

  /* ---------- Recommendations + cross-lane: taxonomy-driven fetch ----------
     Both sections render an empty container synchronously (so there is
     no layout shift and no-JS visitors simply see nothing extra beyond
     Shopify's own SSR recommendations - the tier rules that matter,
     same-or-cleaner recommendations and the collapsed cross-lane, are
     enforced server-side too, not only in this script).

     Primary path: query the Storefront API directly (Shopify's own
     recommendation engine knows nothing about occasion/recipient/theme -
     verified live, an 18th-birthday mug recommended "Computer Whisperer").
     Priority order per product-recommendations mode ("same-lane", tier <=
     viewed) / cross-lane mode ("cross-lane", tier > viewed), first match
     wins, deduped, capped at 8, current product excluded by handle:
       1. same primary_occasion AND overlapping recipient
       2. same primary_occasion
       3. overlapping recipient AND overlapping theme
       4. overlapping theme
     Falls back to Shopify's recommendations endpoint (data-url) only if
     the Storefront call itself fails - a successful call that finds
     nothing renders nothing (no empty collection sections), it does not
     fall back to an off-taxonomy set. */

  function getStorefrontConfig() {
    var shop = document.body.getAttribute("data-storefront-shop");
    var token = document.body.getAttribute("data-storefront-token");
    if (!shop || !token) return null;
    return {
      shop: shop,
      token: token,
      apiVersion: document.body.getAttribute("data-storefront-api-version") || "2026-07",
    };
  }

  var TIER_ORDER = ["clean", "banter", "explicit"];

  function tierMetafieldFilters(tiers) {
    return tiers.map(function (t) {
      return { productMetafield: { namespace: "ostrich", key: "tier", value: t } };
    });
  }

  function tierFiltersUpTo(tier) {
    var idx = TIER_ORDER.indexOf(tier);
    if (idx < 0) idx = TIER_ORDER.length - 1;
    return tierMetafieldFilters(TIER_ORDER.slice(0, idx + 1));
  }

  function tierFiltersAbove(tier) {
    var idx = TIER_ORDER.indexOf(tier);
    if (idx < 0) idx = TIER_ORDER.length - 1;
    return tierMetafieldFilters(TIER_ORDER.slice(idx + 1));
  }

  function metafieldFiltersFor(key, values) {
    return (values || []).map(function (v) {
      return { productMetafield: { namespace: "ostrich", key: key, value: v } };
    });
  }

  /* Builds the priority-ordered filter sets described above. Each entry
     is a full productFilters array for one Storefront `search()` call;
     same-key filters OR together, different keys AND together (verified
     live against this API version - see ostrich-search/src/storefront.ts,
     which this mirrors client-side).

     "Same occasion" prefers ostrich.primary_occasion (single value, the
     occasion the product is genuinely FOR) and falls back to the
     ostrich.occasion list when the product has no primary. primary_occasion
     was registered as a Search & Discovery filter on 2026-08-17; before
     that it was silently ignored as a productMetafield filter, which is
     why the first version of this used the list. Shopify ignores
     unregistered metafield filters without error, so if recommendations
     ever go generic again, check the filter is still registered before
     touching this code. */
  function buildPriorityQueries(productData, tierFilterList) {
    var queries = [];
    var occasionValues = productData.primaryOccasion
      ? [productData.primaryOccasion]
      : productData.occasion;
    var occasionKey = productData.primaryOccasion ? "primary_occasion" : "occasion";
    var hasOccasion = occasionValues.length > 0;
    var hasRecipient = productData.recipient.length > 0;
    var hasTheme = productData.theme.length > 0;
    var occasionFilter = metafieldFiltersFor(occasionKey, occasionValues);
    var recipientFilters = metafieldFiltersFor("recipient", productData.recipient);
    var themeFilters = metafieldFiltersFor("theme", productData.theme);

    if (hasOccasion && hasRecipient) {
      queries.push(occasionFilter.concat(recipientFilters, tierFilterList));
    }
    if (hasOccasion) {
      queries.push(occasionFilter.concat(tierFilterList));
    }
    if (hasRecipient && hasTheme) {
      queries.push(recipientFilters.concat(themeFilters, tierFilterList));
    }
    if (hasTheme) {
      queries.push(themeFilters.concat(tierFilterList));
    }
    return queries;
  }

  var RECS_METAFIELD_IDENTIFIERS =
    '{namespace: "ostrich", key: "tier"}, {namespace: "ostrich", key: "caption"}, ' +
    '{namespace: "ostrich", key: "occasion"}, {namespace: "ostrich", key: "recipient"}, ' +
    '{namespace: "ostrich", key: "theme"}';

  var RECS_QUERY =
    "query OstrichRecs($filters: [ProductFilter!], $first: Int!) { " +
    "search(query: \"*\", types: [PRODUCT], first: $first, productFilters: $filters) { " +
    "edges { node { ... on Product { id handle title onlineStoreUrl " +
    "featuredImage { url } images(first: 2) { edges { node { url } } } " +
    "priceRange { minVariantPrice { amount currencyCode } } " +
    "metafields(identifiers: [" +
    RECS_METAFIELD_IDENTIFIERS +
    "]) { key value } } } } } }";

  function runStorefrontSearch(cfg, filters, first) {
    return fetch("https://" + cfg.shop + "/api/" + cfg.apiVersion + "/graphql.json", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Shopify-Storefront-Access-Token": cfg.token,
      },
      body: JSON.stringify({ query: RECS_QUERY, variables: { filters: filters, first: first } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("storefront_bad_status");
        return res.json();
      })
      .then(function (data) {
        if (data.errors && data.errors.length) throw new Error("storefront_graphql_error");
        var edges = (data.data && data.data.search && data.data.search.edges) || [];
        return edges.map(function (edge) { return edge.node; });
      });
  }

  function normalizeStorefrontNode(node) {
    var byKey = {};
    (node.metafields || []).forEach(function (mf) {
      if (mf) byKey[mf.key] = mf.value;
    });
    var tier = byKey.tier === "banter" || byKey.tier === "explicit" ? byKey.tier : "clean";
    var caption = byKey.caption ? byKey.caption.replace(/\s+/g, " ").trim() : "";
    var occasion = safeJsonParse(byKey.occasion) || [];
    var recipient = safeJsonParse(byKey.recipient) || [];
    var theme = safeJsonParse(byKey.theme) || [];
    var facet = occasion[0] || recipient[0] || theme[0] || "";
    var images = (node.images && node.images.edges) || [];
    var titleFallback = (node.title || "").split(" | ")[0].trim().replace(/ Mug$/, "");
    var priceInfo = node.priceRange ? node.priceRange.minVariantPrice : null;
    return {
      handle: node.handle,
      url: node.onlineStoreUrl || "/products/" + node.handle,
      tier: tier,
      imageUrl: node.featuredImage ? node.featuredImage.url : images[0] ? images[0].node.url : null,
      imageUrl2: images[1] ? images[1].node.url : null,
      facetLabel: facet ? facet.replace(/-/g, " ") : "",
      title: caption || titleFallback,
      price: priceInfo ? priceInfo.amount : null,
      currencyCode: priceInfo ? priceInfo.currencyCode : null,
    };
  }

  function readProductTaxonomy(el) {
    var primary = (el.getAttribute("data-product-primary-occasion") || "").trim();
    return {
      handle: el.getAttribute("data-product-handle") || "",
      tier: el.getAttribute("data-product-tier") || "clean",
      primaryOccasion: primary,
      occasion: safeJsonParse(el.getAttribute("data-product-occasion")) || [],
      recipient: safeJsonParse(el.getAttribute("data-product-recipient")) || [],
      theme: safeJsonParse(el.getAttribute("data-product-theme")) || [],
    };
  }

  /* Runs each priority query in turn, stopping once 8 unique (deduped,
     current-product-excluded) candidates have been collected - only
     falling through to a lower-priority query when the higher one hasn't
     filled the cap. */
  function runPriorityQueries(cfg, priorityQueries, excludeHandle) {
    var seen = {};
    var out = [];
    var i = 0;
    function next() {
      if (out.length >= 8 || i >= priorityQueries.length) return Promise.resolve(out);
      var filters = priorityQueries[i++];
      return runStorefrontSearch(cfg, filters, 12).then(function (nodes) {
        nodes.forEach(function (node) {
          if (out.length >= 8) return;
          if (node.handle === excludeHandle) return;
          if (seen[node.id]) return;
          seen[node.id] = true;
          out.push(node);
        });
        return next();
      });
    }
    return next();
  }

  function buildAndRunTaxonomyRecs(cfg, productData, mode) {
    var tierFilterList = mode === "cross-lane" ? tierFiltersAbove(productData.tier) : tierFiltersUpTo(productData.tier);
    if (mode === "cross-lane" && tierFilterList.length === 0) return Promise.resolve([]);
    var priorityQueries = buildPriorityQueries(productData, tierFilterList);
    if (priorityQueries.length === 0) return Promise.resolve([]);
    return runPriorityQueries(cfg, priorityQueries, productData.handle);
  }

  function renderRecommendationSection(container, mode, rawNodes) {
    if (!rawNodes || !rawNodes.length) return; /* leave SSR fallback content standing */
    var products = rawNodes.map(normalizeStorefrontNode);

    if (mode === "same-lane") {
      var head = document.createElement("div");
      head.className = "section-head";
      var h2 = document.createElement("h2");
      h2.textContent = container.getAttribute("data-heading") || "";
      head.appendChild(h2);
      var ul = document.createElement("ul");
      ul.className = "product-grid";
      products.forEach(function (p) { ul.appendChild(buildProductCard(p)); });
      container.innerHTML = "";
      container.appendChild(head);
      container.appendChild(ul);
      return;
    }

    if (mode === "cross-lane") {
      var panelId = container.getAttribute("data-panel-id") || container.id + "-panel";
      var wrap = document.createElement("div");
      wrap.className = "cross-lane";
      wrap.setAttribute("data-tier", "banter");

      var toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "cross-lane__toggle";
      toggle.setAttribute("data-cross-lane-toggle", "");
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-controls", panelId);

      var copy = document.createElement("span");
      var strong = document.createElement("strong");
      strong.textContent = container.getAttribute("data-title") || "";
      var body = document.createElement("span");
      body.className = "small cross-lane__body";
      body.textContent = container.getAttribute("data-body") || "";
      copy.appendChild(strong);
      copy.appendChild(body);

      var cta = document.createElement("span");
      cta.className = "btn btn--secondary";
      cta.textContent = container.getAttribute("data-cta") || "";

      toggle.appendChild(copy);
      toggle.appendChild(cta);

      var panel = document.createElement("div");
      panel.className = "cross-lane__panel";
      panel.id = panelId;
      panel.setAttribute("data-open", "false");
      var crossUl = document.createElement("ul");
      crossUl.className = "product-grid";
      products.forEach(function (p) { crossUl.appendChild(buildProductCard(p, { deferImage: true })); });
      panel.appendChild(crossUl);

      wrap.appendChild(toggle);
      wrap.appendChild(panel);
      container.innerHTML = "";
      container.appendChild(wrap);
      initCrossLane();
    }
  }

  function fetchShopifyRecommendationsFallback(container) {
    var url = container.getAttribute("data-url");
    if (!url) return;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var next = doc.getElementById(container.id);
        if (next && next.innerHTML.trim() !== "") {
          container.innerHTML = next.innerHTML;
          initCrossLane();
        }
      })
      .catch(function () {
        /* leave the container empty; nothing to gate, nothing shown */
      });
  }

  function initRecommendations() {
    document.querySelectorAll("[data-recommendations]").forEach(function (container) {
      var mode = container.getAttribute("data-recs-mode");
      var cfg = getStorefrontConfig();
      var productMain = document.querySelector("[data-product-main]");
      if (!mode || !cfg || !productMain) {
        fetchShopifyRecommendationsFallback(container);
        return;
      }
      var productData = readProductTaxonomy(productMain);
      buildAndRunTaxonomyRecs(cfg, productData, mode)
        .then(function (nodes) {
          renderRecommendationSection(container, mode, nodes);
        })
        .catch(function () {
          fetchShopifyRecommendationsFallback(container);
        });
    });
  }

  /* ---------- Cross-lane offer ---------- */

  function initCrossLane() {
    document.querySelectorAll("[data-cross-lane-toggle]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var panel = document.getElementById(btn.getAttribute("aria-controls"));
        if (!panel) return;
        var open = panel.getAttribute("data-open") === "true";
        panel.setAttribute("data-open", open ? "false" : "true");
        btn.setAttribute("aria-expanded", open ? "false" : "true");
        if (!open) {
          sessionStorage.setItem("ostrich_cross_lane_seen", "1");
          var lazyImgs = panel.querySelectorAll("img[data-src]");
          lazyImgs.forEach(function (img) {
            img.src = img.getAttribute("data-src");
            img.removeAttribute("data-src");
          });
        }
      });
    });
  }

  /* ---------- Conversational AI search ----------
     Header search box (desktop + mobile) becomes the AI box, POSTing to
     the ostrich-search Worker and rendering results into a full-width
     panel (dropdown, below the header) using the shared buildProductCard
     renderer above. The /search page enhances its own on-page form the
     same way, into an inline panel, and additionally auto-runs once on
     load against the ?q= the Shopify search template already resolved -
     see sections/search-results.liquid's data-initial-query.

     Fallback (no JS / Worker unreachable / non-200 / 4s timeout): every
     [data-ai-search-form] is a real <form action="{{ routes.search_url }}">
     already, so failing over is just letting/forcing that native submit
     through - the exact same result a no-JS visitor gets. Tier safety is
     belt-and-braces only here (data-tier + the html.tier-* CSS guard,
     theme.css) - the Worker enforces tier server-side; this only protects
     against a stale cached response outliving a Dial change mid-session. */

  /* ---------- Cloudflare Turnstile: invisible token for the AI search POST ----------
     One hidden widget, rendered lazily on first use in execution:'execute'
     mode (see layout/theme.liquid's render=explicit script tag), so there
     is never a visible challenge in the header. getTurnstileToken()
     resolves a fresh token (or null) before every AI search request -
     Turnstile tokens are single-use and expire (~300s), so calling
     turnstile.execute() again per search is required, not optional.
     Never blocks or breaks search: if the script didn't load (network
     block, extension), or a token doesn't arrive within
     TURNSTILE_TOKEN_TIMEOUT_MS, this resolves null and runAiSearch()
     sends the request without a token - the Worker (src/turnstile.ts) is
     what decides what happens next, never this code. */

  var TURNSTILE_TOKEN_TIMEOUT_MS = 3000;
  var turnstileWidgetId = null;
  var turnstileUnavailable = false;

  function getTurnstileToken() {
    return new Promise(function (resolve) {
      if (turnstileUnavailable || typeof window.turnstile === "undefined") {
        resolve(null);
        return;
      }

      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve(null);
      }, TURNSTILE_TOKEN_TIMEOUT_MS);

      function done(token) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(token || null);
      }

      try {
        if (turnstileWidgetId === null) {
          var siteKey = document.body.getAttribute("data-turnstile-site-key");
          if (!siteKey) {
            turnstileUnavailable = true;
            done(null);
            return;
          }
          var container = document.createElement("div");
          container.style.display = "none";
          document.body.appendChild(container);
          turnstileWidgetId = window.turnstile.render(container, {
            sitekey: siteKey,
            action: "ostrich-ai-search",
            execution: "execute",
            "response-field": false,
            callback: done,
            "error-callback": function () { done(null); },
            "timeout-callback": function () { done(null); },
          });
          if (typeof turnstileWidgetId === "undefined") {
            turnstileUnavailable = true;
            done(null);
            return;
          }
        } else {
          window.turnstile.reset(turnstileWidgetId);
        }
        window.turnstile.execute(turnstileWidgetId);
      } catch (e) {
        turnstileUnavailable = true;
        done(null);
      }
    });
  }

  var aiSearchPanels = {}; /* panelId -> { controller, triggerInput } */

  function aiSearchPanelIsDismissible(panel) {
    return panel.hasAttribute("data-ai-search-dismissible");
  }

  function openAiSearchPanel(panel, triggerInput) {
    if (aiSearchPanelIsDismissible(panel)) panel.hidden = false;
    aiSearchPanels[panel.id].triggerInput = triggerInput;
  }

  function closeAiSearchPanel(panel) {
    if (!aiSearchPanelIsDismissible(panel)) return;
    panel.hidden = true;
    var entry = aiSearchPanels[panel.id];
    if (entry && entry.triggerInput) entry.triggerInput.focus();
  }

  function showAiSearchSkeleton(panel) {
    var chipsEl = panel.querySelector("[data-ai-search-chips]");
    var statusEl = panel.querySelector("[data-ai-search-status]");
    var resultsEl = panel.querySelector("[data-ai-search-results]");
    if (chipsEl) chipsEl.innerHTML = "";
    if (statusEl) statusEl.innerHTML = "";
    if (!resultsEl) return;
    resultsEl.innerHTML = "";
    resultsEl.setAttribute("aria-busy", "true");
    for (var i = 0; i < 4; i++) {
      var li = document.createElement("li");
      var card = document.createElement("div");
      card.className = "skeleton-card";
      var media = document.createElement("div");
      media.className = "skeleton skeleton-media";
      var line1 = document.createElement("div");
      line1.className = "skeleton skeleton-line";
      var line2 = document.createElement("div");
      line2.className = "skeleton skeleton-line";
      line2.style.width = "45%";
      card.appendChild(media);
      card.appendChild(line1);
      card.appendChild(line2);
      li.appendChild(card);
      resultsEl.appendChild(li);
    }
  }

  function renderAiSearchResults(panel, data, triggerInput) {
    var strings = getOstrichStrings();
    var chipsEl = panel.querySelector("[data-ai-search-chips]");
    var statusEl = panel.querySelector("[data-ai-search-status]");
    var resultsEl = panel.querySelector("[data-ai-search-results]");
    if (!chipsEl || !statusEl || !resultsEl) return;

    chipsEl.innerHTML = "";
    statusEl.innerHTML = "";
    resultsEl.innerHTML = "";
    resultsEl.removeAttribute("aria-busy");

    var chips = Array.isArray(data.chips) ? data.chips : [];
    var results = Array.isArray(data.results) ? data.results : [];
    var noInventory = Array.isArray(data.no_inventory) ? data.no_inventory : [];

    if (data.degraded) {
      var degradedLine = document.createElement("p");
      degradedLine.className = "small mono ai-search-status-line";
      degradedLine.textContent = strings.degraded || "Showing plain search results";
      statusEl.appendChild(degradedLine);
    } else if (chips.length) {
      chips.forEach(function (c) {
        var chip = document.createElement("span");
        chip.className = "mono ai-search-chip";
        chip.textContent = c;
        chipsEl.appendChild(chip);
      });
      var notThat = document.createElement("button");
      notThat.type = "button";
      notThat.className = "mono ai-search-not-that";
      notThat.textContent = strings.notThat || "Not that?";
      notThat.addEventListener("click", function () {
        closeAiSearchPanel(panel);
        if (triggerInput) {
          triggerInput.focus();
          if (triggerInput.select) triggerInput.select();
        }
      });
      chipsEl.appendChild(notThat);
    }

    var noInventoryTemplate = strings.noInventory || "Nothing {{ value }}-themed yet.";
    noInventory.forEach(function (label) {
      var p = document.createElement("p");
      p.className = "small mono ai-search-status-line";
      p.textContent = noInventoryTemplate.replace(/\{\{\s*value\s*\}\}/, label);
      statusEl.appendChild(p);
    });

    if (!results.length) {
      /* Reuses .empty-state (theme.css) - same mascot-beside-copy layout
         as the server-rendered empty search/filter states below it. */
      var emptyWrap = document.createElement("div");
      emptyWrap.className = "empty-state";
      if (strings.noResultsMascotUrl) {
        var mascotImg = document.createElement("img");
        mascotImg.src = strings.noResultsMascotUrl;
        mascotImg.alt = "";
        mascotImg.setAttribute("aria-hidden", "true");
        mascotImg.className = "state-mascot state-mascot--small";
        mascotImg.width = 120;
        mascotImg.height = 120;
        mascotImg.loading = "lazy";
        mascotImg.addEventListener("error", function () { mascotImg.remove(); });
        emptyWrap.appendChild(mascotImg);
      }
      var empty = document.createElement("p");
      empty.className = "small ai-search-empty";
      empty.textContent = strings.noResults || "Nothing matched that. Try a person, an occasion, or a swear word.";
      emptyWrap.appendChild(empty);
      resultsEl.appendChild(emptyWrap);
      return;
    }

    results.forEach(function (r) {
      var tier = r.tier === "banter" || r.tier === "explicit" ? r.tier : "clean";
      resultsEl.appendChild(
        buildProductCard({
          handle: r.handle,
          url: r.url,
          tier: tier,
          imageUrl: r.imageUrl,
          imageUrl2: r.imageUrl2,
          facetLabel: r.primaryOccasion ? String(r.primaryOccasion).replace(/-/g, " ") : "",
          title: r.displayTitle || r.caption || r.title || "",
          price: r.price,
          currencyCode: r.currencyCode,
        }),
      );
    });
  }

  /* isAutoRun: the /search page's own load-time run against ?q=. A
     failure there must NOT trigger a form submit (that would reload the
     page the visitor is already on) - it just leaves the panel empty, so
     the server-rendered Shopify results already in the page (search
     -results.liquid's own search.performed block) stand as the fallback,
     exactly as if JS had never run.

     Race safety (both bugs previously caused the results panel to flicker
     between populated and empty/hidden with no visible cause):
     - One request per distinct (query, tier): entry.pendingKey dedupes a
       duplicate call for the request already in flight (or already
       served) into a no-op that just keeps the panel open, instead of
       firing a second network request and re-running the skeleton.
     - A new (query, tier) aborts the previous controller AND every
       handler below - success (.then) as well as failure (.catch) -
       bails out if entry.controller no longer matches the controller it
       closed over. Only the .catch path had this guard before; the
       success path didn't, so a superseded-but-not-yet-aborted request
       could win the race and overwrite a newer request's results with
       stale (often empty) data. Tier changes never reach this function at
       all - the Dial re-filters existing cards client-side via their
       data-tier attribute (theme.css's html.tier-* rules), so switching
       tiers never re-triggers a fetch or touches this dedupe/abort logic. */
  function runAiSearch(form, panelId, query, triggerInput, isAutoRun) {
    var panel = document.getElementById(panelId);
    if (!panel) {
      if (!isAutoRun && form) HTMLFormElement.prototype.submit.call(form);
      return;
    }
    if (!aiSearchPanels[panelId]) aiSearchPanels[panelId] = {};
    var entry = aiSearchPanels[panelId];
    var tier = getCookie(TIER_COOKIE) || "explicit";
    var key = tier + "::" + query;

    if (entry.pendingKey === key) {
      /* Already in flight (or already served) for this exact (query,
         tier) - nothing new to fetch, just make sure the panel showing
         it is open. */
      openAiSearchPanel(panel, triggerInput);
      return;
    }

    var previousController = entry.controller;
    var controller = new AbortController();
    entry.controller = controller;
    entry.pendingKey = key;
    if (previousController) previousController.abort();

    openAiSearchPanel(panel, triggerInput);
    showAiSearchSkeleton(panel);

    var sanitizedQuery = query.slice(0, AI_SEARCH_MAX_QUERY_LENGTH);

    /* One attempt at the network call, given a (possibly null) token.
       isRetry caps this at a single Turnstile retry - see the 403 branch
       below - so this can recurse at most once. */
    function attempt(token, isRetry) {
      var timeoutId = setTimeout(function () { controller.abort(); }, AI_SEARCH_TIMEOUT_MS);
      function clear() { clearTimeout(timeoutId); }

      return fetch(AI_SEARCH_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query: sanitizedQuery, tier: tier, turnstileToken: token || undefined }),
        signal: controller.signal,
      }).then(function (res) {
        if (res.status === 403 && !isRetry) {
          /* Missing/stale/rejected token (src/turnstile.ts reasons all
             contain "turnstile") - re-execute Turnstile once for a fresh
             token and retry this search exactly once before falling
             back to the plain form submit like any other failure. Any
             other 403 (e.g. origin_not_allowed) falls straight through
             to the generic failure path below. */
          return res
            .json()
            .catch(function () { return {}; })
            .then(function (errBody) {
              clear();
              var reason = errBody && errBody.error;
              if (typeof reason === "string" && reason.indexOf("turnstile") !== -1) {
                if (entry.controller !== controller) return null;
                return getTurnstileToken().then(function (freshToken) {
                  return attempt(freshToken, true);
                });
              }
              throw new Error("ai_search_bad_status");
            });
        }
        clear();
        if (!res.ok) throw new Error("ai_search_bad_status");
        return res.json();
      }, function (err) {
        /* fetch() itself rejected (network error, or our own abort on
           timeout) before a response ever arrived - still clear the
           timer so it can't fire a no-op abort() later, then propagate. */
        clear();
        throw err;
      });
    }

    getTurnstileToken()
      .then(function (token) {
        return attempt(token, false);
      })
      .then(function (data) {
        /* null: a retry was superseded by a newer search - that newer
           search owns rendering now, nothing to do here. Superseded by a
           newer request in general - a stale success must never
           overwrite what that newer request already rendered (or is
           still rendering). */
        if (!data) return;
        if (entry.controller !== controller) return;
        renderAiSearchResults(panel, data, triggerInput);
      })
      .catch(function () {
        /* A newer search already replaced this one - this abort is stale, not a failure. */
        if (entry.controller !== controller) return;
        var resultsEl = panel.querySelector("[data-ai-search-results]");
        if (resultsEl) resultsEl.removeAttribute("aria-busy");
        if (isAutoRun) {
          if (resultsEl) resultsEl.innerHTML = "";
          var chipsEl = panel.querySelector("[data-ai-search-chips]");
          var statusEl = panel.querySelector("[data-ai-search-status]");
          if (chipsEl) chipsEl.innerHTML = "";
          if (statusEl) statusEl.innerHTML = "";
          return;
        }
        if (form) HTMLFormElement.prototype.submit.call(form);
      });
  }

  function initAiSearch() {
    var template = document.querySelector("[data-product-card-template]");
    if (!template) return;

    document.querySelectorAll("[data-ai-search-form]").forEach(function (form) {
      var panelId = form.getAttribute("data-ai-search-form");
      var input = form.querySelector("[data-ai-search-input]");
      if (!input) return;
      form.addEventListener("submit", function (e) {
        var query = (input.value || "").trim();
        if (!query) return; /* let the native GET submit happen, same as today */
        e.preventDefault();
        runAiSearch(form, panelId, query, input, false);
      });
    });

    var inlinePanel = document.getElementById("AiSearchInline");
    if (inlinePanel) {
      var initialQuery = (inlinePanel.getAttribute("data-initial-query") || "").trim();
      if (initialQuery) {
        var inlineForm = document.querySelector('[data-ai-search-form="AiSearchInline"]');
        var inlineInput = inlineForm ? inlineForm.querySelector("[data-ai-search-input]") : null;
        runAiSearch(inlineForm, "AiSearchInline", initialQuery, inlineInput, true);
      }
    }

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      document.querySelectorAll("[data-ai-search-dismissible]").forEach(function (panel) {
        if (!panel.hidden) closeAiSearchPanel(panel);
      });
    });
  }

  /* ---------- Product gallery ---------- */

  function initGallery() {
    document.querySelectorAll("[data-gallery]").forEach(function (gallery) {
      var main = gallery.querySelector("[data-gallery-main]");
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll("[data-gallery-thumb]"));
      if (!main || !thumbs.length) return;
      // The 360 spin, if this product has one. It is a sibling of the main
      // <img> rather than something swapped into its src, so selecting it
      // means showing one and hiding the other.
      var videoWrap = gallery.querySelector("[data-gallery-video]");
      var video = videoWrap ? videoWrap.querySelector("video") : null;

      function showVideo() {
        if (!videoWrap) return;
        videoWrap.hidden = false;
        main.hidden = true;
        // A muted autoplay loop normally starts on its own; play() covers
        // the case where the element was hidden when the page loaded and
        // the browser declined. It can reject, and a rejected promise with
        // no handler is an unhandled rejection in the console.
        if (video) { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
      }

      function showPhoto(src, alt) {
        if (videoWrap) {
          videoWrap.hidden = true;
          if (video) video.pause();
        }
        main.hidden = false;
        if (src) main.src = src;
        if (alt) main.alt = alt;
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          if (thumb.getAttribute("data-media-type") === "video") {
            showVideo();
          } else {
            showPhoto(thumb.getAttribute("data-full"), thumb.getAttribute("data-alt"));
          }
          thumbs.forEach(function (t) { t.setAttribute("aria-pressed", t === thumb ? "true" : "false"); });
        });
      });
    });
  }

  /* ---------- Quantity stepper ---------- */

  function initQuantity() {
    document.querySelectorAll("[data-qty-field]").forEach(function (field) {
      var input = field.querySelector("input");
      var minus = field.querySelector("[data-qty-minus]");
      var plus = field.querySelector("[data-qty-plus]");
      if (!input) return;
      function clamp(v) {
        var min = parseInt(input.min || "1", 10);
        return Math.max(min, v);
      }
      if (minus) minus.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
      if (plus) plus.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  /* ---------- Variant picker ----------
     Keeps the hidden variant id, price and button state in sync with the
     selected options. Server-rendered radios already reflect the
     currently selected variant, so this only needs to run on change;
     without JS the form still submits the variant that was selected on
     load (the page just will not update mid-visit). */

  function initVariantPicker() {
    document.querySelectorAll("[data-product-form]").forEach(function (form) {
      var variantsScript = form.querySelector("[data-product-variants]");
      var optionInputs = Array.prototype.slice.call(form.querySelectorAll("[data-option-input]"));
      if (!variantsScript || !optionInputs.length) return;

      var variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        return;
      }

      var idInput = form.querySelector("[data-product-form-id]");
      var submitBtn = form.querySelector("[data-product-form-submit]");
      var priceWrap = document.querySelector("[data-product-price]");

      function currentOptions() {
        var byPosition = {};
        optionInputs.forEach(function (input) {
          if (input.checked) byPosition[input.getAttribute("data-option-position")] = input.value;
        });
        var positions = Object.keys(byPosition).map(Number).sort(function (a, b) { return a - b; });
        return positions.map(function (p) { return byPosition[p]; });
      }

      function matchVariant() {
        var opts = currentOptions();
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (v.options.length === opts.length && v.options.every(function (o, idx) { return o === opts[idx]; })) {
            return v;
          }
        }
        return null;
      }

      function update() {
        var variant = matchVariant();
        if (!variant) {
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = submitBtn.getAttribute("data-label-unavailable");
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceWrap) {
          var priceEl = priceWrap.querySelector(".price");
          if (priceEl) priceEl.textContent = variant.priceHtml;
        }
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          submitBtn.textContent = variant.available
            ? submitBtn.getAttribute("data-label-add")
            : submitBtn.getAttribute("data-label-sold-out");
        }
        showVariantMedia(variant);
      }

      /* Move the gallery to the variant's own photograph.

         BY CLICKING THE THUMB, not by writing to the main <img>. initGallery
         already owns "show this photo" -- it hides the 360 video, sets the
         src and the alt, and moves aria-pressed -- and a second copy here
         would be two writers of one rule, disagreeing the first time either
         changes. The thumb is the gallery's own control; this presses it.

         Matched on the MEDIA ID, not the alt text. Alt is already a join key
         between our record and Shopify's media and it is one that has bitten
         this repo before; inside one page the id is exact and free.

         Silent when the variant has no image of its own: that is the ordinary
         one-variant product, where the gallery is already showing the only
         thing there is. */
      function showVariantMedia(variant) {
        if (!variant.mediaId) return;
        var thumb = document.querySelector(
          '[data-gallery-thumb][data-media-id="' + variant.mediaId + '"]');
        if (!thumb) return;
        if (thumb.getAttribute("aria-pressed") === "true") return;
        thumb.click();
      }

      optionInputs.forEach(function (input) {
        input.addEventListener("change", update);
      });

      /* AND THE OTHER DIRECTION. Alan, 2026-08-28: "while you fixed clicking
         the variant moves the gallery, clicking the gallery doesnt move the
         variant, both need to work".

         He is right, and on a grouped listing it is the half that matters
         more: the gallery is 23 badges, so clicking one IS choosing one. A
         buyer who clicks the Welsh dragon and then Add to Basket would have
         been sent the England mug -- the picture said one thing and the
         hidden variant id said another, which is worse than the gallery not
         moving at all.

         Driven from HERE rather than from initGallery, for the same reason
         the forward direction is driven from here: the gallery owns "show
         this photo" and knows nothing about variants, and teaching it would
         put variant logic in two places. */
      var variantByMedia = {};
      variants.forEach(function (v) {
        if (v.mediaId) variantByMedia[String(v.mediaId)] = v;
      });
      document.querySelectorAll("[data-gallery-thumb][data-media-id]")
        .forEach(function (thumb) {
          thumb.addEventListener("click", function () {
            var v = variantByMedia[thumb.getAttribute("data-media-id")];
            if (!v) return;          // a scene shot, not any variant's own
            var changed = false;
            optionInputs.forEach(function (input) {
              var pos = Number(input.getAttribute("data-option-position"));
              var want = v.options[pos - 1];
              if (want === undefined) return;
              if (input.value === want) {
                if (!input.checked) { input.checked = true; changed = true; }
              } else if (input.checked) {
                input.checked = false;
              }
            });
            // NO EVENT DISPATCH, and no loop: `update()` is called directly.
            // Dispatching `change` would re-enter through the listener above,
            // and update() clicks the matching thumb -- which is this thumb,
            // whose handler would run again. The aria-pressed guard already
            // stops that recursing, but not relying on a guard to break a
            // cycle is cheaper than reasoning about whether it always will.
            if (changed) update();
          });
        });
    });
  }

  /* ---------- Hero: lifestyle photo carousel ----------
     Crossfades between the slides sections/hero.liquid renders (up to
     six), pauses on hover/focus, and is entirely inert under
     prefers-reduced-motion (CSS already keeps slide 0 statically visible
     and hides the dots in that case - this function just never runs, so
     there is no timer and no rotation to disable).
     Never assumes the photos exist: bails immediately if .hero carries
     hero--no-photo (set by the first slide's onerror in the Liquid), and
     keeps watching for that class in case the image fails after this has
     already started (a slow 404 can resolve after DOMContentLoaded). */
  function initHeroCarousel() {
    var hero = document.querySelector("[data-hero]");
    if (!hero) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (hero.classList.contains("hero--no-photo")) return;

    var photo = hero.querySelector("[data-hero-photo]");
    var dotsWrap = hero.querySelector("[data-hero-dots]");
    var status = hero.querySelector("[data-hero-status]");
    if (!photo) return;

    var index = 0;
    var timer = null;

    function slides() {
      return Array.prototype.slice.call(photo.querySelectorAll("[data-hero-slide]"));
    }
    function dots() {
      return dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll("[data-hero-dot]")) : [];
    }

    function goTo(i) {
      var s = slides();
      if (!s.length) return;
      index = ((i % s.length) + s.length) % s.length;
      s.forEach(function (slide, si) {
        slide.classList.toggle("is-active", si === index);
      });
      dots().forEach(function (dot, di) {
        var on = di === index;
        dot.classList.toggle("is-active", on);
        dot.setAttribute("aria-current", on ? "true" : "false");
      });
      if (status) {
        var template = status.getAttribute("data-hero-status-template") || "";
        status.textContent = template
          .replace("__CURRENT__", index + 1)
          .replace("__TOTAL__", s.length);
      }
    }

    function next() {
      if (slides().length > 1) goTo(index + 1);
    }

    function start() {
      stop();
      if (slides().length > 1) timer = setInterval(next, 6000);
    }
    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    dots().forEach(function (dot) {
      dot.addEventListener("click", function () {
        goTo(parseInt(dot.getAttribute("data-hero-dot"), 10));
        start();
      });
    });

    hero.addEventListener("mouseenter", stop);
    hero.addEventListener("mouseleave", start);
    hero.addEventListener("focusin", stop);
    hero.addEventListener("focusout", function (e) {
      if (!hero.contains(e.relatedTarget)) start();
    });

    /* Belt and braces: if the first slide's onerror fires after this has
       already started (a slow-resolving 404), stop rotating and let the
       CSS fallback take over rather than crossfading an empty layer. */
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (hero.classList.contains("hero--no-photo")) {
          stop();
          mo.disconnect();
        }
      });
      mo.observe(hero, { attributes: true, attributeFilter: ["class"] });
    }

    goTo(0);
    start();
  }

  /* ---------- Mascot idle: blink + head bob ----------
     The bob is a plain looping CSS animation (theme.css). This only
     handles the blink's randomised 4-7s cadence, since a CSS keyframe
     loop can't vary its own period. Toggles .is-blinking for ~180ms on
     the mascot container (never per-image) so it works identically
     whichever of the three PNG states is currently showing. */
  function initMascotIdle() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    document.querySelectorAll(".dial-mascot").forEach(function (mascot) {
      function scheduleBlink() {
        var delay = 4000 + Math.random() * 3000;
        setTimeout(function () {
          mascot.classList.add("is-blinking");
          setTimeout(function () {
            mascot.classList.remove("is-blinking");
          }, 200);
          scheduleBlink();
        }, delay);
      }
      scheduleBlink();
    });
  }

  /* ---------- Wall reveal: homepage product cards fade up once ----------
     sections/featured-collection.liquid only adds data-wall-reveal when
     template.name == 'index', so this never runs on collection/search
     grids. Stagger is computed per IntersectionObserver callback batch
     (roughly "this row just entered the viewport"), not by the card's
     absolute position in the grid, so it can never run past the 300ms
     per-row cap the brief sets regardless of how far down the wall a
     card sits. */
  function initWallReveal() {
    var cards = document.querySelectorAll("[data-wall-reveal] .product-card");
    if (!cards.length) return;
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      cards.forEach(function (card) { card.classList.add("is-revealed"); });
      return;
    }
    var observer = new IntersectionObserver(function (entries, obs) {
      var entering = entries.filter(function (entry) { return entry.isIntersecting; });
      entering.forEach(function (entry, i) {
        var delay = Math.min(i, 7) * 40;
        entry.target.style.transitionDelay = delay + "ms";
        entry.target.classList.add("is-revealed");
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: "0px 0px -10% 0px" });
    cards.forEach(function (card) { observer.observe(card); });
  }

  /* ---------- Header nav dropdowns ---------- */

  /* Called since 3ff740d (2026-08-18) but never written, so it threw a
     ReferenceError on every page load -- and because it is the SECOND call in
     the DOMContentLoaded block, every init after it never ran. Facets, dial,
     AI search, gallery, quantity, variant picker, cart, recommendations and
     the hero carousel were all dead site-wide for two days.

     The nav is <details>, so it opens and closes with no JS at all. This is
     enhancement only, matching the file's stated contract: close the other
     desktop dropdowns when one opens, and close on outside click or Escape.
     The mobile menu's groups share data-nav-dropdown but are a deliberate
     accordion inside an already-open panel, so they are left alone. */

  function initNavDropdowns() {
    var desktop = Array.prototype.slice.call(
      document.querySelectorAll(".site-header__nav [data-nav-dropdown]"));
    if (!desktop.length) return;

    function closeAll(except) {
      desktop.forEach(function (d) { if (d !== except) d.open = false; });
    }

    desktop.forEach(function (d) {
      d.addEventListener("toggle", function () { if (d.open) closeAll(d); });
    });

    document.addEventListener("click", function (e) {
      if (!desktop.some(function (d) { return d.contains(e.target); })) closeAll(null);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      var open = desktop.filter(function (d) { return d.open; });
      if (!open.length) return;
      closeAll(null);
      var s = open[0].querySelector("summary");
      if (s) s.focus();
    });
  }

  /* ---------- Cart quantity change: submit form (no external cart drawer) ---------- */

  function initCartForm() {
    var cartForm = document.querySelector("[data-cart-form]");
    if (!cartForm) return;
    cartForm.querySelectorAll("input[data-cart-qty]").forEach(function (input) {
      input.addEventListener("change", function () {
        cartForm.submit();
      });
    });
  }

  /* Each init is isolated. The missing initNavDropdowns above proved the
     failure mode: one throw in this list silently killed every feature below
     it, and the page still looked fine because nothing here logs. Now a
     broken init costs its own feature and names itself in the console. */
  document.addEventListener("DOMContentLoaded", function () {
    [
      ["mobileNav", initMobileNav],
      ["navDropdowns", initNavDropdowns],
      ["facets", initFacets],
      ["dial", initDial],
      ["headerDialSync", initHeaderDialSync],
      ["aiSearch", initAiSearch],
      ["cardMedia", initCardMedia],
      ["crossLane", initCrossLane],
      ["gallery", initGallery],
      ["quantity", initQuantity],
      ["variantPicker", initVariantPicker],
      ["cartForm", initCartForm],
      ["recommendations", initRecommendations],
      ["heroCarousel", initHeroCarousel],
      ["mascotIdle", initMascotIdle],
      ["wallReveal", initWallReveal]
    ].forEach(function (pair) {
      try {
        pair[1]();
      } catch (err) {
        if (window.console && console.error) {
          console.error("[ostrich] init " + pair[0] + " failed:", err);
        }
      }
    });
  });
})();
